import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  ANY_SENIORITY_CODE,
  WizardClientMatter,
  WizardInitialAssignment,
  WizardPreferred,
  WizardRequestor,
  WizardResult,
} from '../../../models/request-wizard.models';
import {
  CreateRequestRequest,
  RequestDto,
} from '../../../models/request.models';
import { AuthService } from '../../../services/auth.service';
import {
  ClientMatterInfoDto,
} from '../../../services/matter.service';
import { RequestService } from '../../../services/request.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { describeApiError } from '../../../utils/api-error.utils';
import {
  UnsavedChangesDialogComponent,
  UnsavedChangesDialogData,
} from '../../shared/unsaved-changes-dialog/unsaved-changes-dialog.component';
import {
  ValidationErrorsDialogComponent,
  ValidationErrorsDialogData,
} from '../../shared/validation-errors-dialog/validation-errors-dialog.component';
import { RequestWizardBodyComponent } from './request-wizard-body.component';

export interface RequestWizardDialogData {
  mode: 'create' | 'edit';
  /** Required when mode === 'edit'. Used to load the existing request. */
  requestId?: number;
}

@Component({
  selector: 'app-request-wizard-dialog',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    OverlayModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    RequestWizardBodyComponent,
  ],
  templateUrl: './request-wizard-dialog.component.html',
  styleUrls: ['./request-wizard-dialog.component.scss'],
})
export class RequestWizardDialogComponent implements OnInit, AfterViewInit {
  dialogRef =
    inject<MatDialogRef<RequestWizardDialogComponent, boolean>>(MatDialogRef);
  data = inject<RequestWizardDialogData>(MAT_DIALOG_DATA);

  private cdr = inject(ChangeDetectorRef);
  private authService = inject(AuthService);
  private requestService = inject(RequestService);
  private snackbarService = inject(SnackbarService);
  private dialog = inject(MatDialog);

  @ViewChild('wizardBody') wizardBody?: RequestWizardBodyComponent;

  isSubmitting = false;
  isLoadingInitial = false;
  initialData: WizardResult | null = null;
  showCreateMenu = false;
  readonly splitButtonWidth = 256;

  readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 0,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: 0,
    },
  ];

  get isCreateMode(): boolean {
    return this.data.mode === 'create';
  }

  get isEditMode(): boolean {
    return this.data.mode === 'edit';
  }

  get dialogTitle(): string {
    return this.isCreateMode ? 'New Request' : 'Edit Request';
  }

  get submitButtonLabel(): string {
    if (this.isSubmitting) {
      return this.isCreateMode ? 'Creating…' : 'Saving…';
    }
    return this.isCreateMode ? 'Create Request' : 'Save Changes';
  }

  /** Step labels in wizard order; index N+1 is the "Next: …" target. */
  private readonly stepLabels = [
    'General',
    'Client & Timing',
    'Resources',
    'Assignments',
  ];

  get currentStepIndex(): number {
    return this.wizardBody?.stepIndex ?? 0;
  }

  get isFinalStep(): boolean {
    return this.currentStepIndex >= this.stepLabels.length - 1;
  }

  get nextStepLabel(): string {
    return `Next: ${this.stepLabels[this.currentStepIndex + 1] ?? ''}`;
  }

  /** Label for the early create/save action in the split-button dropdown. */
  get earlyActionLabel(): string {
    return this.isCreateMode ? 'Create Request' : 'Save Changes';
  }

  ngOnInit(): void {
    if (this.isEditMode) {
      if (this.data.requestId == null) {
        this.snackbarService.showError('Edit mode requires a request ID.');
        this.dialogRef.close(false);
        return;
      }
      this.isLoadingInitial = true;
      this.requestService.getRequestById(this.data.requestId).subscribe({
        next: (dto) => {
          // Matter names are now hydrated by the API in dto.matterDetails;
          // no secondary network call needed.
          this.initialData = this.toWizardResult(dto);
          this.isLoadingInitial = false;
        },
        error: (err) => {
          this.isLoadingInitial = false;
          this.snackbarService.showError(
            describeApiError(err, 'Failed to load request'),
          );
          this.dialogRef.close(false);
        },
      });
    }
  }

  ngAfterViewInit(): void {
    // wizardBody?.stepIndex reads through two @ViewChild layers (dialog →
    // wizardBody → stepper.selectedIndex). Without this, Angular detects a
    // value change mid-cycle and throws NG0100.
    this.cdr.detectChanges();
  }

  toggleCreateMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.showCreateMenu = !this.showCreateMenu;
  }

  onCreateRequestFromMenu(): void {
    this.showCreateMenu = false;
    this.attemptSubmit();
  }

  /**
   * Triggered by the create/save buttons. The button is never disabled by
   * validation: if the wizard still has issues, surface them in a modal instead
   * of saving; otherwise hand off to the wizard body to submit.
   */
  attemptSubmit(): void {
    if (this.isSubmitting) {
      return;
    }
    if (!this.wizardBody?.canSubmit()) {
      // Light up the offending fields (red error state) behind the dialog.
      this.wizardBody?.revealValidationErrors();
      const issues = this.wizardBody?.getValidationIssues() ?? [];
      const data: ValidationErrorsDialogData = {
        title: this.isCreateMode
          ? 'Cannot Create Request'
          : 'Cannot Save Changes',
        message: issues.length
          ? 'Complete the following before saving your changes:'
          : 'Complete all required fields before saving your changes.',
        issues,
      };
      this.dialog.open(ValidationErrorsDialogComponent, {
        width: '500px',
        data,
      });
      return;
    }
    this.wizardBody?.onSubmit();
  }

  onSubmit(result: WizardResult): void {
    this.isSubmitting = true;
    const payload = this.buildPayload(result);
    const op$ = this.isCreateMode
      ? this.requestService.createRequest(payload)
      : this.requestService.updateRequest(this.data.requestId!, payload);
    const successMsg = this.isCreateMode
      ? 'Request created successfully'
      : 'Request updated successfully';
    const failurePrefix = this.isCreateMode
      ? 'Failed to create request'
      : 'Failed to update request';

    op$.subscribe({
      next: () => {
        this.dialogRef.close(true);
        this.snackbarService.showSuccess(successMsg);
      },
      error: (error) => {
        this.isSubmitting = false;
        // The API's sentence (the 409s on save, say) wins over our own copy
        this.snackbarService.showError(
          describeApiError(error, failurePrefix),
        );
      },
    });
  }

  onCancel(): void {
    if (this.isSubmitting) {
      return;
    }

    if (!this.wizardBody?.hasUnsavedChanges()) {
      this.dialogRef.close(false);
      return;
    }

    const dialogData: UnsavedChangesDialogData = {
      entityName: 'Request',
      continueEditingOnly: true,
    };
    const confirmRef = this.dialog.open(UnsavedChangesDialogComponent, {
      width: '500px',
      disableClose: true,
      data: dialogData,
    });
    confirmRef.afterClosed().subscribe((result) => {
      if (result === 'discard') {
        this.dialogRef.close(false);
      }
      // 'cancel' or undefined: keep the wizard open.
    });
  }

  private buildPayload(result: WizardResult): CreateRequestRequest {
    const user = this.authService.getCurrentUserValue();
    const requestedResources = result.resources.requestedResources.map((r) => ({
      seniorityCode: r.seniorityCode,
      count: r.count,
    }));
    // The API derives the total from the breakdown; we still send the sum for the
    // transition (the RequestedResourceCount column is retained until the QA drop).
    const requestedResourceCount = requestedResources.reduce(
      (sum, r) => sum + (r.count || 0),
      0,
    );
    return {
      description: result.describe.description,
      requestedResourceCount,
      requestedResources,
      matterRequestedResources: result.assignments.matterRequestedResources,
      requestorEmployeeIds: result.describe.requestors.map((r) => r.id),
      submittedByEmployeeId: user?.employeeId ?? undefined,
      requestType: result.describe.requestType,
      status: 'NEW',
      startDate: result.clientTiming.startDate
        ? new Date(result.clientTiming.startDate).toISOString()
        : undefined,
      endDate: result.clientTiming.endDate
        ? new Date(result.clientTiming.endDate).toISOString()
        : undefined,
      preferredEmployeeIds: result.resources.preferred.map((p) => p.id),
      clientMatterNumber:
        result.clientTiming.clientMatters[0]?.clientMatterNumber,
      clientMatterNumbers: result.clientTiming.clientMatters.map(
        (m) => m.clientMatterNumber,
      ),
      practiceAreaCodes: result.describe.practiceAreaCodes,
      initialAssignments: result.assignments.initialAssignments.map((a) => ({
        employeeId: a.employeeId,
        assignmentTypeCode: a.assignmentTypeCode,
        clientMatterNumbers: a.clientMatterNumbers,
      })),
    };
  }

  /**
   * Returns the request's matter numbers, preferring the new array field and
   * falling back to the deprecated scalar for legacy data.
   */
  private getMatterNumbers(dto: RequestDto): string[] {
    if (dto.clientMatterNumbers && dto.clientMatterNumbers.length > 0) {
      return dto.clientMatterNumbers;
    }
    return dto.clientMatterNumber ? [dto.clientMatterNumber] : [];
  }

  private toWizardResult(dto: RequestDto): WizardResult {
    const currentUserId = this.authService.getCurrentUserValue()?.employeeId;

    const requestors: WizardRequestor[] = (dto.requestors ?? []).map((r) => ({
      id: r.employeeId,
      name: r.fullName || `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim(),
      role: r.title,
      isCurrentUser: r.employeeId === currentUserId,
      practiceAreaCodes: r.practiceAreaCodes,
    }));

    const infoMap = new Map<string, ClientMatterInfoDto>(
      (dto.matterDetails ?? []).map((info) => [info.clientMatterNumber, info]),
    );
    const clientMatters: WizardClientMatter[] = this.getMatterNumbers(dto).map(
      (num) => {
        const [clientNumber, matterNumber] = num.split('-');
        const info = infoMap.get(num);
        return {
          clientNumber: info?.clientNumber ?? clientNumber ?? '',
          clientName: info?.clientName ?? dto.clientName ?? '',
          matterNumber: matterNumber ?? '',
          matterName: info?.matterName ?? dto.matterTitle ?? '',
          clientMatterNumber: num,
        };
      },
    );

    const preferred: WizardPreferred[] = (dto.preferredEmployees ?? []).map(
      (emp: any) => {
        const firstName = emp?.firstName ?? emp?.FirstName ?? '';
        const lastName = emp?.lastName ?? emp?.LastName ?? '';
        const composedName = `${firstName} ${lastName}`.trim();
        const name =
          composedName ||
          emp?.fullName ||
          emp?.FullName ||
          emp?.name ||
          emp?.Name ||
          '';
        const id =
          emp?.id ?? emp?.Id ?? emp?.employeeId ?? emp?.employeeID ?? '';
        return {
          id: String(id),
          name: String(name),
          role: String(emp?.title ?? emp?.Title ?? 'Unknown'),
          email: emp?.email ?? emp?.Email ?? undefined,
          seniorityCode: emp?.seniorityCode ?? emp?.SeniorityCode ?? undefined,
          seniority: emp?.seniority ?? emp?.Seniority ?? undefined,
        };
      },
    );

    // Deduplicate by employeeId — if the same employee has both PRIMARY and
    // COVERAGE rows (bad data), prefer the non-PRIMARY (COVERAGE) entry.
    const allMatterNumbers = clientMatters.map((m) => m.clientMatterNumber);
    const assignmentMap = new Map<string, WizardInitialAssignment>();
    for (const a of dto.assignedResourcesWithType ?? []) {
      const typeCode =
        a.assignmentTypeCategory === 'COVERAGE' ? 'COVERAGE' : 'PRIMARY';
      const existing = assignmentMap.get(a.employeeId);
      if (!existing || typeCode === 'COVERAGE') {
        const matters =
          a.clientMatterNumbers && a.clientMatterNumbers.length > 0
            ? a.clientMatterNumbers
            : [...allMatterNumbers];
        assignmentMap.set(a.employeeId, {
          employeeId: a.employeeId,
          name: a.fullName || `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim(),
          role: a.title ?? '',
          assignmentTypeCode: typeCode,
          clientMatterNumbers: matters,
          seniorityCode: a.seniorityCode,
          seniority: a.seniority,
        });
      }
    }
    const initialAssignments: WizardInitialAssignment[] = Array.from(
      assignmentMap.values(),
    );

    return {
      describe: {
        description: dto.description,
        requestType: dto.requestType?.code ?? 'PRIMARY',
        requestors,
        practiceAreaCodes: dto.practiceAreaCodes ?? [],
      },
      clientTiming: {
        clientMatters,
        startDate: dto.startDate ? new Date(dto.startDate) : null,
        endDate: dto.endDate ? new Date(dto.endDate) : null,
      },
      resources: {
        // Use the persisted per-seniority breakdown; fall back to a single "Any"
        // row from the stored total for legacy/not-yet-backfilled requests.
        requestedResources:
          dto.requestedResources && dto.requestedResources.length > 0
            ? dto.requestedResources.map((r) => ({
                count: r.count,
                seniorityCode: r.seniorityCode,
              }))
            : [
                {
                  count: dto.requestedResourceCount ?? 1,
                  seniorityCode: ANY_SENIORITY_CODE,
                },
              ],
        preferred,
      },
      assignments: {
        initialAssignments,
        matterRequestedResources: dto.matterRequestedResources ?? {},
      },
    };
  }
}
